import React from 'react';
import { 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Box,
  Tooltip
} from '@mui/material';
import NoteIcon from '@mui/icons-material/Note';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Deal } from './DealPieChart';
import { renderStatusChip, formatDate, isWithinSevenDays } from './RecentDealsList';

interface DealSummaryTableProps {
  deals: Deal[];
  title?: string;
  maxHeight?: number;
  onNotesClick?: (event: React.MouseEvent<HTMLButtonElement>, deal: Deal) => void;
  onCofClick?: (event: React.MouseEvent<HTMLButtonElement>, deal: Deal) => void;
  onBfsStageClick?: (event: React.MouseEvent<HTMLButtonElement>, deal: Deal) => void;
  onDeleteClick?: (deal: Deal) => void;
}

const formatElapsedTime = (seconds: number | null): string => {
  if (!seconds) return 'No time data';
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  if (minutes === 0) {
    return `${remainingSeconds} sec`;
  }
  
  return `${minutes} min ${remainingSeconds} sec`;
};

const DealSummaryTable: React.FC<DealSummaryTableProps> = ({ 
  deals, 
  title = "Deal Summary", 
  maxHeight = 440,
  onNotesClick,
  onCofClick,
  onBfsStageClick,
  onDeleteClick
}) => {
  return (
    <Paper elevation={3} sx={{ p: 0, overflow: 'hidden' }}>
      <Typography variant="h6" sx={{ p: 2, bgcolor: (theme) => theme.palette.grey[100] }}>
        {title}
      </Typography>
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader aria-label="deals table">
          <TableHead>
            <TableRow sx={{ height: '15px' }}>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>Deal Number</TableCell>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>BFS Stage</TableCell>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>Mortgage Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>Mortgage Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>Term</TableCell>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>LTV</TableCell>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>COF Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 'bold', padding: '2px 8px', fontSize: '0.75rem' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deals.slice(0, 10).map((deal, index) => {
              const isClosingSoon = isWithinSevenDays(deal.closing_date);
              
              return (
                <TableRow hover key={`deal-${deal.id}-${index}`} sx={{ height: '15px' }}>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>{deal.deal_number}</TableCell>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                    <Box display="flex" alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption" sx={{ mr: 1, fontSize: '0.7rem' }}>
                        {deal.bfs_stage}
                      </Typography>
                      {onBfsStageClick && (
                        <Tooltip title="Edit BFS Stage">
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ padding: '1px' }}
                            onClick={(e) => onBfsStageClick(e, deal)}
                          >
                            <EditIcon sx={{ fontSize: '0.75rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>{deal.mortgage_amount}</TableCell>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>{deal.mtg_type}</TableCell>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>{deal.term}</TableCell>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>{deal.ltv}</TableCell>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                    <Box display="flex" alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isClosingSoon ? 'error.main' : 'inherit', 
                          fontWeight: isClosingSoon ? 'bold' : 'normal',
                          mr: 1,
                          fontSize: '0.7rem'
                        }}
                      >
                        {formatDate(deal.cof)}
                      </Typography>
                      {onCofClick && (
                        <Tooltip title="Edit COF Date">
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ padding: '1px' }}
                            onClick={(e) => onCofClick(e, deal)}
                          >
                            <EditIcon sx={{ fontSize: '0.75rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                    {onNotesClick && (
                      <Tooltip 
                        title={
                          <Box>
                            {deal.elapsed_time && (
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                                Elapsed time: {formatElapsedTime(deal.elapsed_time)}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {deal.notes ? deal.notes : "No notes available"}
                            </Typography>
                          </Box>
                        }
                        arrow 
                        placement="top-start"
                        enterDelay={500}
                        leaveDelay={200}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          sx={{ padding: '1px' }}
                          onClick={(e) => onNotesClick(e, deal)}
                          aria-label="View/Edit Notes"
                        >
                          <NoteIcon sx={{ fontSize: '0.75rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                    {onDeleteClick && (
                      <Tooltip title="Delete Deal">
                        <IconButton
                          size="small"
                          color="error"
                          sx={{ padding: '1px' }}
                          onClick={() => onDeleteClick(deal)}
                        >
                          <DeleteIcon sx={{ fontSize: '0.75rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {deals.length === 0 && (
              <TableRow key="no-deals-found" sx={{ height: '15px' }}>
                <TableCell colSpan={9} align="center" sx={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                  <Typography variant="body1" color="text.secondary">
                    No deals found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DealSummaryTable; 